import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Flex, Text, Button, TextField, IconButton, Separator, Spinner } from "@radix-ui/themes";
import { ArrowRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetTripadvisorLocationMappings, useSaveTripadvisorLocationMappings } from "hooks";

export function TripadvisorReviewsConfigSetupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();

  const { orgId } = location.state || {};
  const { userProfile } = useProfileContext();
  const { initialItems } = useLocationContext();

  const organisationId = orgId || userProfile?.organisation_id;

  const [locationMappings, setLocationMappings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { data: existingMappingsData, isLoading: isMappingsLoading, error: mappingsError } =
    useGetTripadvisorLocationMappings(organisationId, !!organisationId);

  const saveMappings = useSaveTripadvisorLocationMappings();

  const existingMappingsByLocationId = useMemo(() => {
    const mappings = existingMappingsData?.data?.mappings || [];
    const byId = {};
    (Array.isArray(mappings) ? mappings : []).forEach((m) => {
      const locId = m.location ?? m.location_id;
      if (!locId) return;
      byId[String(locId)] = m.url_path || "";
    });
    return byId;
  }, [existingMappingsData]);

  useEffect(() => {
    if (!organisationId) return;
    if (hasInitialized) return;

    // Initialize rows for all b-connect locations (keep empty if no existing mapping)
    const next = {};
    (initialItems || []).forEach((loc) => {
      next[String(loc.value)] = existingMappingsByLocationId[String(loc.value)] || "";
    });

    // Also include mappings that exist but the location isn’t in initialItems yet
    Object.entries(existingMappingsByLocationId || {}).forEach(([locId, urlPath]) => {
      if (next[locId] === undefined) next[locId] = urlPath || "";
    });

    setLocationMappings(next);
    setHasInitialized(true);
  }, [organisationId, hasInitialized, initialItems, existingMappingsByLocationId]);

  const handleUrlPathChange = (bconnectLocationId, value) => {
    setLocationMappings((prev) => ({
      ...(prev || {}),
      [String(bconnectLocationId)]: value,
    }));
  };

  const handleSaveAndFinish = async () => {
    try {
      setIsLoading(true);

      const mappingsToSave = Object.entries(locationMappings || {})
        .map(([locationId, urlPath]) => {
          const trimmed = String(urlPath || "").trim();
          if (!trimmed) return null;
          return {
            location_id: parseInt(locationId, 10),
            url_path: trimmed,
          };
        })
        .filter(Boolean);

      if (mappingsToSave.length === 0) {
        setToastMessage("Please map at least one location");
        return;
      }

      const result = await saveMappings.mutateAsync({
        organisation_id: organisationId,
        mappings: mappingsToSave,
      });

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.error || "Failed to save mappings");
      }

      queryClient.invalidateQueries(["tripadvisor-reviews-status"]);
      queryClient.invalidateQueries(["tripadvisor-location-mappings"]);

      navigate("/activation-success", {
        state: {
          description: "Your Tripadvisor locations are now connected. Reviews will be synced automatically.",
          integrationType: "tripadvisor-reviews",
        },
      });
    } catch (error) {
      console.error("Error during TripAdvisor setup:", error);
      setToastMessage(error?.message || "Failed to complete Tripadvisor setup");
    } finally {
      setIsLoading(false);
    }
  };

  if (!organisationId) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: "100vh" }}>
        <Text>No organisation data found. Please go back and try again.</Text>
      </Flex>
    );
  }

  return (
    <Flex style={{ minHeight: "100vh" }} justify="center" align="center" p="6">
      <Card style={{ maxWidth: "900px", width: "100%" }} p="4">
        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Header */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" justify="between" align="center">
            <Flex display="flex" align="center" gap="4">
              <Flex
                style={{
                  borderRadius: "var(--space-1)",
                  background: "#FFFFFF",
                  border: "1px solid var(--gray-a6)",
                }}
                height="40px"
                width="40px"
                p="2"
                justify="center"
                align="center"
              >
                <img
                  src={require("../../../assets/images/tripadvisor.png")}
                  alt="Tripadvisor"
                  style={{ width: "24px" }}
                />
              </Flex>
              <Flex direction="column" justify="center" align="start">
                <Text size="4" weight="medium">
                  Tripadvisor
                </Text>
                <Text style={{ color: "var(--gray-a11)" }} size="1">
                  Customer reviews
                </Text>
              </Flex>
            </Flex>

            <Flex display="flex" align="center" gap="4">
              <Button size="2" variant="soft" onClick={() => navigate("/integrations/browse-all")} color="gray">
                Cancel
              </Button>
              <Button size="2" variant="soft" onClick={handleSaveAndFinish} loading={isLoading}>
                Save and finish
              </Button>
            </Flex>
          </Flex>

          <Separator orientation="horizontal" size="4" my="4" />

          {/* Locations Section */}
          <Flex style={{ alignSelf: "stretch" }} display="flex" align="start" gap="5">
            <Flex style={{ alignSelf: "stretch" }} display="flex" width="280px" direction="column" align="start" gap="2">
              <Text size="2" weight="medium">
                Location Mapping
              </Text>
              <Text size="1" weight="regular" style={{ color: "var(--gray-a11)" }}>
                Map each b connect location to its Tripadvisor venue page by pasting the venue URL (or the url_path).
              </Text>
              <Text size="1" weight="regular" style={{ color: "var(--gray-a11)" }}>
                Example: https://www.tripadvisor.com/Restaurant_Review-...html
              </Text>
            </Flex>

            <Flex direction="column" gap="3" style={{ width: "100%" }}>
              {isMappingsLoading && (
                <Flex justify="center" align="center" py="6">
                  <Spinner size="2" />
                  <Text size="2" color="gray" style={{ marginLeft: "8px" }}>
                    Loading existing mappings...
                  </Text>
                </Flex>
              )}

              {mappingsError && (
                <Flex justify="center" align="center" py="6">
                  <Text size="2" color="red">
                    Failed to load existing mappings. You can still enter mappings and save.
                  </Text>
                </Flex>
              )}

              {!isMappingsLoading && (
                <>
                  <Flex direction="row" justify="between" style={{ width: "100%" }}>
                    <Text size="2" weight="medium" style={{ width: "45%" }}>
                      b connect location
                    </Text>
                    <Text size="2" weight="medium" style={{ width: "45%", paddingLeft: "40px" }}>
                      Tripadvisor url_path
                    </Text>
                  </Flex>

                  {(initialItems || []).map((loc) => (
                    <Flex key={loc.value} direction="row" justify="between" align="center" style={{ width: "100%" }}>
                      <Flex style={{ width: "45%", minWidth: 0 }}>
                        <Flex
                          style={{
                            width: "100%",
                            backgroundColor: "var(--gray-3)",
                            border: "1px solid var(--gray-a7)",
                            borderRadius: "var(--radius-2)",
                            padding: "0 var(--space-2)",
                            height: "34px",
                            alignItems: "center",
                            overflow: "hidden",
                          }}
                        >
                          <Text
                            size="2"
                            title={loc.label}
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              width: "100%",
                              color: "var(--gray-a11)",
                            }}
                          >
                            {loc.label}
                          </Text>
                        </Flex>
                      </Flex>

                      <Flex align="center" style={{ paddingLeft: "8px", paddingRight: "8px" }}>
                        <IconButton size="2" variant="ghost" color="gray" style={{ pointerEvents: "none" }}>
                          <ArrowRightIcon width="16" height="16" />
                        </IconButton>
                      </Flex>

                      <Flex style={{ width: "45%", minWidth: 0, maxWidth: "45%", flex: "0 0 45%" }}>
                        <TextField.Root
                          value={locationMappings[String(loc.value)] || ""}
                          onChange={(e) => handleUrlPathChange(loc.value, e.target.value)}
                          placeholder="https://www.tripadvisor.com/Restaurant_Review-...html"
                          size="2"
                          variant="surface"
                          style={{ width: "100%" }}
                        />
                      </Flex>
                    </Flex>
                  ))}
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Card>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Flex>
  );
}
